import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import ClockWidget from "@/components/ClockWidget";
import ManualEntryForm from "@/components/ManualEntryForm";
import PeriodNavigator from "@/components/PeriodNavigator";
import EntriesTable from "@/components/EntriesTable";
import CommissionsSection from "@/components/CommissionsSection";
import { fetchActiveProjects } from "@/components/ProjectPicker";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import {
  todayISO,
  formatHours,
  parseISODate,
  toISODate,
} from "@/lib/dateUtils";
import { useAuth } from "@/context/AuthContext";
import { Loader2, FileText, Timer, Eye, X as XIcon, FlaskConical } from "lucide-react";
import { toast } from "sonner";

// Client-side period filter for sandbox preview entries (mirrors the backend's
// Mon–Sun week math from date_utils.period_range so the totals tile and entry
// list line up with what a real freelancer sees on the same date.)
function inPeriod(entryDate, period, refISO) {
  if (!entryDate) return false;
  if (period === "day") return entryDate === refISO;
  if (period === "month") return entryDate.startsWith(refISO.substring(0, 7));
  const ref = parseISODate(refISO);
  if (!ref) return false;
  const weekday = ref.getDay() === 0 ? 6 : ref.getDay() - 1;
  const start = new Date(ref);
  start.setDate(ref.getDate() - weekday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return entryDate >= toISODate(start) && entryDate <= toISODate(end);
}

function previewId() {
  return `preview-${Math.random().toString(36).slice(2, 10)}`;
}

async function resolveProject(projectId) {
  if (!projectId) return { project_id: null, project_name: null, project_color: null };
  try {
    const list = await fetchActiveProjects();
    const p = list.find((x) => x.id === projectId);
    return {
      project_id: projectId,
      project_name: p?.name || null,
      project_color: p?.color || null,
    };
  } catch {
    return { project_id: projectId, project_name: null, project_color: null };
  }
}

export default function FreelancerDashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Admin "View as freelancer" mode: read-only preview of another user's data.
  const asUserId = searchParams.get("as");
  const asUserName = searchParams.get("as_name");
  const isAdminPreview = user?.role === "admin" && !!asUserId;
  // Admin sandbox: full freelancer UI, but every action stays in component
  // memory. Backend writes are blocked server-side by require_entry_writer
  // anyway; this just keeps the UX honest about it.
  const isSandbox = user?.role === "admin" && searchParams.get("preview") === "1" && !asUserId;

  const [period, setPeriod] = useState("day");
  const [refDate, setRefDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Sandbox-only state: in-memory entries and timer that never touch the API.
  const [previewEntries, setPreviewEntries] = useState([]);
  const [previewTimer, setPreviewTimer] = useState(null);

  const fetchParams = useMemo(() => {
    const p = { period, date: refDate };
    if (isAdminPreview) p.as_user_id = asUserId;
    return p;
  }, [period, refDate, isAdminPreview, asUserId]);

  const loadEntries = useCallback(async () => {
    if (isSandbox) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/entries", { params: fetchParams });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load entries.");
    } finally {
      setLoading(false);
    }
  }, [fetchParams, isSandbox]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Sandbox handlers: synthesize entries client-side, never hit the API.
  const handleSandboxClockStart = useCallback(async ({ activity, project_id }) => {
    const proj = await resolveProject(project_id);
    setPreviewTimer({
      activity,
      ...proj,
      started_at: new Date().toISOString(),
    });
    toast.success("Timer started", { description: "Preview mode — nothing was saved." });
  }, []);

  const handleSandboxClockStop = useCallback(() => {
    setPreviewTimer((t) => {
      if (!t) return t;
      const elapsedH = Math.max(
        (Date.now() - new Date(t.started_at).getTime()) / 3_600_000,
        0.01,
      );
      const hours = Math.round(elapsedH * 100) / 100;
      const entry = {
        id: previewId(),
        date: todayISO(),
        hours,
        activity: t.activity,
        project_id: t.project_id,
        project_name: t.project_name,
        project_color: t.project_color,
        source: "timer",
        status: "approved",
        approved_by: null,
        deleted_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setPreviewEntries((rows) => [entry, ...rows]);
      toast.success("Time logged", {
        description: `Preview mode — ${hours}h not saved.`,
      });
      return null;
    });
  }, []);

  const handleSandboxAddEntry = useCallback(async ({ date, hours, activity, project_id }) => {
    const proj = await resolveProject(project_id);
    const entry = {
      id: previewId(),
      date,
      hours,
      activity,
      ...proj,
      source: "manual",
      status: "approved",
      approved_by: null,
      deleted_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setPreviewEntries((rows) => [entry, ...rows]);
    toast.success("Entry added", { description: "Preview mode — nothing was saved." });
  }, []);

  const handleSandboxUpdateEntry = useCallback(async (id, { date, hours, activity, project_id }) => {
    const proj = await resolveProject(project_id);
    setPreviewEntries((rows) =>
      rows.map((r) =>
        r.id === id
          ? { ...r, date, hours, activity, ...proj, updated_at: new Date().toISOString() }
          : r,
      ),
    );
    toast.success("Entry updated", { description: "Preview mode — nothing was saved." });
  }, []);

  const handleSandboxDeleteEntry = useCallback((id) => {
    setPreviewEntries((rows) => rows.filter((r) => r.id !== id));
    toast.success("Entry deleted", { description: "Preview mode — nothing was saved." });
  }, []);

  // Admin who lands on /dashboard without ?as= or ?preview=1 isn't supposed to
  // see their own (admin) data here — bounce them to the admin dashboard.
  if (user?.role === "admin" && !asUserId && !isSandbox) {
    return <Navigate to="/admin" replace />;
  }

  // Sandbox totals are computed from the in-memory list; real mode reads them
  // off the API response.
  const filteredPreview = isSandbox
    ? previewEntries.filter((e) => inPeriod(e.date, period, refDate))
    : null;
  const totalHours = isSandbox
    ? Math.round(filteredPreview.reduce((sum, e) => sum + (e.hours || 0), 0) * 100) / 100
    : data?.total_hours || 0;
  const count = isSandbox ? filteredPreview.length : data?.entry_count || 0;

  const previewName = asUserName ? decodeURIComponent(asUserName) : "freelancer";

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8"
        data-testid="freelancer-dashboard"
      >
        {isAdminPreview && (
          <div
            className="mb-5 rounded-xl border p-3 flex flex-wrap items-center justify-between gap-3"
            style={{
              background: `${BRAND.purple}10`,
              borderColor: `${BRAND.purple}55`,
            }}
            data-testid="view-as-banner"
          >
            <div className="flex items-center gap-2 text-sm">
              <Eye size={16} style={{ color: BRAND.purple }} />
              <span className="text-slate-700">
                Viewing as <strong>{previewName}</strong> · read-only preview
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              data-testid="view-as-exit"
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-white transition"
            >
              <XIcon size={13} />
              Exit preview
            </button>
          </div>
        )}

        {isSandbox && (
          <div
            className="mb-5 rounded-xl border p-3 flex flex-wrap items-center justify-between gap-3"
            style={{
              background: `${BRAND.teal}10`,
              borderColor: `${BRAND.teal}55`,
            }}
            data-testid="sandbox-banner"
          >
            <div className="flex items-center gap-2 text-sm">
              <FlaskConical size={16} style={{ color: BRAND.teal }} />
              <span className="text-slate-700">
                <strong>Freelancer preview</strong> · clock, entries, edits, and deletes work
                normally but nothing is saved. Refresh to clear.
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              data-testid="sandbox-exit"
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-white transition"
            >
              <XIcon size={13} />
              Exit preview
            </button>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900">
            {isAdminPreview
              ? `${previewName}'s timesheet`
              : isSandbox
              ? "Freelancer preview"
              : `Welcome back, ${user?.name?.split(" ")[0] || "there"} 👋`}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {isAdminPreview
              ? "What this freelancer sees on their dashboard."
              : isSandbox
              ? "Try the freelancer experience end-to-end. Nothing here writes to the database."
              : "Track your time with a live timer or log entries manually."}
          </p>
        </div>

        {/* Row 1: Clock + Manual — hidden in admin-preview (read-only of another user) */}
        {!isAdminPreview && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ClockWidget
              onEntryLogged={loadEntries}
              previewMode={isSandbox}
              previewTimer={isSandbox ? previewTimer : null}
              onPreviewStart={handleSandboxClockStart}
              onPreviewStop={handleSandboxClockStop}
            />
            <ManualEntryForm
              onEntryLogged={loadEntries}
              previewMode={isSandbox}
              onPreviewSubmit={handleSandboxAddEntry}
            />
          </div>
        )}

        {/* Commissions: only when enabled for the freelancer; hidden in admin
            preview-as and sandbox modes (those flows aren't real users). */}
        {!isAdminPreview && !isSandbox && user?.commissions_enabled && (
          <CommissionsSection />
        )}

        {/* Row 2: Period navigator + totals */}
        <div className="mt-8">
          <PeriodNavigator
            period={period}
            setPeriod={setPeriod}
            refDate={refDate}
            setRefDate={setRefDate}
          />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className="rounded-2xl p-5 text-white"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wider font-semibold">
                <Timer size={14} />
                Total hours
              </div>
              <div
                className="mt-1 text-3xl font-bold tabular-nums"
                data-testid="total-hours"
              >
                {formatHours(totalHours)}
              </div>
              <div className="mt-1 text-sm text-white/90">
                in selected {period}
              </div>
            </div>
            <div className="rounded-2xl p-5 bg-white border border-slate-200">
              <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
                <FileText size={14} />
                Entries
              </div>
              <div
                className="mt-1 text-3xl font-bold text-slate-900 tabular-nums"
                data-testid="entry-count"
              >
                {count}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {data?.start_date}{data?.start_date !== data?.end_date ? ` – ${data?.end_date}` : ""}
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Entries table */}
        <div className="mt-5">
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-slate-400" size={20} />
            </div>
          ) : error ? (
            <div
              className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm"
              data-testid="entries-error"
            >
              {error}
            </div>
          ) : (
            <EntriesTable
              entries={isSandbox ? filteredPreview : data?.entries || []}
              onChanged={loadEntries}
              readOnly={isAdminPreview}
              previewMode={isSandbox}
              onPreviewUpdate={handleSandboxUpdateEntry}
              onPreviewDelete={handleSandboxDeleteEntry}
            />
          )}
        </div>
      </main>
    </div>
  );
}
